import React, { useState, useEffect } from 'react';
import { Avatar } from 'antd';
import {
    UserOutlined,
    ArrowLeftOutlined,
    PhoneOutlined,
    VideoCameraOutlined,
    InfoCircleOutlined,
    CameraFilled,
    PictureOutlined,
    AudioOutlined,
    SmileOutlined,
    PlusCircleOutlined,
    WifiOutlined,
} from '@ant-design/icons';

// Simple Battery Icon Component
const BatteryIcon = () => (
    <div style={{
        width: 22, height: 10, border: '1px solid #000', borderRadius: 2, position: 'relative', display: 'flex', alignItems: 'center', padding: 1
    }}>
        <div style={{ width: '80%', height: '100%', background: '#000', borderRadius: 1 }}></div>
        <div style={{ width: 2, height: 4, background: '#000', position: 'absolute', right: -4, borderTopRightRadius: 2, borderBottomRightRadius: 2 }}></div>
    </div>
);

// Simple Signal Icon Component
const SignalIcon = () => (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 10 }}>
        <div style={{ width: 3, height: 4, background: '#000', borderRadius: 1 }}></div>
        <div style={{ width: 3, height: 6, background: '#000', borderRadius: 1 }}></div>
        <div style={{ width: 3, height: 8, background: '#000', borderRadius: 1 }}></div>
        <div style={{ width: 3, height: 10, background: '#000', borderRadius: 1 }}></div>
    </div>
);

// Typing Animation Component for Keywords
function TypingKeywords({ keywords }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [displayText, setDisplayText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (keywords.length === 0) {
            setDisplayText('');
            return;
        }

        if (keywords.length === 1) {
            setDisplayText(keywords[0]);
            return;
        }

        const currentKeyword = keywords[currentIndex];
        const typingSpeed = isDeleting ? 50 : 100;
        const pauseTime = isDeleting ? 200 : 1500;

        let timeout;

        if (!isDeleting && displayText === currentKeyword) {
            timeout = setTimeout(() => setIsDeleting(true), pauseTime);
        } else if (isDeleting && displayText === '') {
            setIsDeleting(false);
            setCurrentIndex((prev) => (prev + 1) % keywords.length);
        } else {
            timeout = setTimeout(() => {
                if (isDeleting) {
                    setDisplayText(currentKeyword.substring(0, displayText.length - 1));
                } else {
                    setDisplayText(currentKeyword.substring(0, displayText.length + 1));
                }
            }, typingSpeed);
        }

        return () => clearTimeout(timeout);
    }, [displayText, isDeleting, currentIndex, keywords]);

    // Reset animation when keywords list changes (only if multiple keywords)
    useEffect(() => {
        if (keywords.length > 1) {
            setCurrentIndex(0);
            setDisplayText('');
            setIsDeleting(false);
        }
    }, [keywords]);

    if (keywords.length === 0) return null;

    return (
        <span>
            {displayText}
            {keywords.length > 1 && (
                <span style={{
                    borderRight: '2px solid #fff',
                    animation: 'blink 1s step-end infinite',
                    marginLeft: 1
                }} />
            )}
        </span>
    );
}

export default function ChatPreview({ flowData }) {
    if (!flowData) return null;

    // Get the profile picture from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.user_id;
    const botAvatarUrl = userId
        ? `https://public-bucket-v3.s3.ap-south-1.amazonaws.com/instandm/pro_pic/${userId}/pro_pic.jpg`
        : 'https://ui-avatars.com/api/?name=Bot';

    const renderStep = (step, index) => {
        if (step.type === 'message') {
            return (
                <div key={index} className="chat-bubble-container bot">
                    {/* Avatar for Bot */}
                    <Avatar size={28} src={botAvatarUrl} style={{ marginRight: 8, alignSelf: 'flex-end' }} />

                    <div className="chat-content-stack">
                        {step.image_url && (
                            <div className="chat-bubble bot image-bubble">
                                <img src={step.image_url} alt="Sent" className="chat-image" />
                            </div>
                        )}

                        {step.text && (
                            <div className="chat-bubble bot text-bubble">
                                {step.text}
                            </div>
                        )}

                        {/* Render Buttons (Quick Replies Style) */}
                        {step.buttons?.length > 0 && (
                            <div className="chat-quick-replies">
                                {step.buttons.map((btn, i) => (
                                    <div key={i} className="quick-reply-pill">
                                        {btn.label || btn.title}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Templates (Carousel) */}
                        {step.templates?.length > 0 && (
                            <div className="chat-carousel">
                                {step.templates.map((tpl, i) => (
                                    <div key={i} className="carousel-card">
                                        {tpl.thumb_url && <img src={tpl.thumb_url} alt="Card" />}
                                        <div className="card-content">
                                            <div className="card-title">{tpl.title}</div>
                                            <div className="card-subtitle">{tpl.description}</div>
                                        </div>
                                        {tpl.buttons?.map((btn, bi) => (
                                            <div key={bi} className="card-button">{btn.title}</div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        if (step.type === 'delay') {
            const duration = step.duration || 1;
            const unit = step.durationType === 's' ? 'second' : 'minute';
            const label = `⏱ Wait ${duration} ${unit}${duration > 1 ? 's' : ''}`;
            return (
                <div key={index} className="chat-delay-indicator">
                    <div className="delay-label">{label}</div>
                </div>
            );
        }
        if (step.type === 'tag') {
            const tagNames = step.tagsData?.map(t => t.name).join(', ') || 'No tags';
            return (
                <div key={index} className="chat-delay-indicator">
                    <div className="tag-label">🏷️ Tag: {tagNames}</div>
                </div>
            );
        }
        if (step.type === 'question') {
            return (
                <div key={index} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <div className="chat-bubble-container bot">
                        {/* Avatar for Bot */}
                        <Avatar size={28} src={botAvatarUrl} style={{ marginRight: 8, alignSelf: 'flex-end' }} />

                        <div className="chat-content-stack">
                            {step.question && (
                                <div className="chat-bubble bot text-bubble">
                                    {step.question}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Waiting for Input Indicator */}
                    <div className="chat-bubble-container user" style={{ marginTop: 8, opacity: 0.7 }}>
                        <div className="chat-bubble user" style={{
                            background: '#f0f2f5',
                            border: '1px dashed #bbb',
                            color: '#888',
                            boxShadow: 'none',
                            fontStyle: 'italic',
                            fontSize: 13
                        }}>
                            Wait for user reply...
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="chat-preview-container">
            <div className="phone-frame">
                {/* Status Bar */}
                <div className="status-bar">
                    <div className="time">9:41</div>
                    <div className="status-icons">
                        <SignalIcon />
                        <WifiOutlined style={{ fontSize: 14, fontWeight: 'bold' }} />
                        <BatteryIcon />
                    </div>
                </div>

                {/* Header */}
                <div className="phone-header">
                    <ArrowLeftOutlined style={{ fontSize: 20 }} />
                    <div className="header-profile">
                        <Avatar icon={<UserOutlined />} size="default" />
                        <div className="header-text">
                            <div className="header-name">Target User <span className="verified-badge"></span></div>
                            <div className="header-status">Active now</div>
                        </div>
                    </div>
                    <div className="header-actions">
                        <PhoneOutlined style={{ fontSize: 20 }} />
                        <VideoCameraOutlined style={{ fontSize: 20 }} />
                    </div>
                </div>

                {/* Body */}
                <div className="phone-body">
                    <div className="timestamp-separator">Just Now</div>

                    {/* Trigger (User Message) */}
                    {(flowData.trigger?.keywords?.length > 0 || flowData.trigger?.automate_all) && (
                        <div className="chat-bubble-container user">
                            <div className="chat-bubble user">
                                {flowData.trigger?.automate_all ? (
                                    <span style={{ fontStyle: 'italic', opacity: 0.8 }}>
                                        [ Any Message Incoming ]
                                    </span>
                                ) : (
                                    <TypingKeywords keywords={flowData.trigger.keywords} />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Bot Messages (Steps) */}
                    {flowData.steps?.map((step, index) => renderStep(step, index))}

                    {/* Seen Indicator */}
                    {/* <div className="chat-seen">Seen</div> */}
                </div>

                {/* Footer */}
                <div className="phone-footer">
                    <div className="footer-left">
                        <div className="cam-icon-wrapper">
                            <CameraFilled style={{ color: '#fff', fontSize: 16 }} />
                        </div>
                    </div>
                    <div className="footer-input-wrapper">
                        <input type="text" placeholder="Message..." disabled />
                        <div className="input-actions">
                            <AudioOutlined />
                            <PictureOutlined />
                            <SmileOutlined />
                        </div>
                    </div>
                    <div className="footer-right">
                        <PlusCircleOutlined style={{ fontSize: 22, color: '#000' }} />
                    </div>
                </div>

                {/* Home Indicator */}
                <div className="home-indicator"></div>
            </div>
        </div>
    );
}
